import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import { authFetch } from '../utils/authFetch';
import Spinner from '../components/Spinner';
import ConfirmationModal from '../components/ConfirmationModal';
import { toast } from 'react-hot-toast';

const ConstructorListPage = () => {
    const [constructors, setConstructors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [constructorToDelete, setConstructorToDelete] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchConstructors = async () => {
            try {
                const data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/api/constructores`);
                setConstructors(data);
            } catch (err) {
                setError('No se pudieron cargar los constructores. Inténtalo de nuevo más tarde.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchConstructors();
    }, []);

    const handleDeleteClick = (id) => {
        setConstructorToDelete(id);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await authFetch(`${window.REACT_APP_API_ENDPOINT}/api/constructores/${constructorToDelete}`, {
                method: 'DELETE',
            });
            setConstructors(constructors.filter((c) => c.id !== constructorToDelete));
            toast.success('Constructor eliminado correctamente');
            navigate('/constructores');
        } catch (err) {
            setError('No se pudo eliminar el constructor.');
            console.error(err);
        } finally {
            setConfirmOpen(false);
            setConstructorToDelete(null);
        }
    };

    if (loading) return <Spinner text="Cargando constructores..." />;
    if (error) return <div className="text-red-500 text-center p-4">{error}</div>;

    return (
        <div className="container mx-auto p-6">
            <ConfirmationModal
                isOpen={confirmOpen}
                title="Confirmar eliminación"
                message={
                  "¿Estás seguro de que quieres eliminar este constructor? " +
                  "Todas las estimaciones de reforma asociadas a este constructor también serán eliminadas y el constructor dejará de estar disponible para cualquier usuario. " +
                  "Esta acción no se puede deshacer."
                }
                confirmText="Eliminar"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-600 hover:bg-red-700"
                onConfirm={handleConfirmDelete}
                onClose={() => { setConfirmOpen(false); setConstructorToDelete(null); }}
            />
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Gestión de Constructores</h1>
                <Link
                    to="/constructores/nuevo"
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
                >
                    <FaPlus className="mr-2" /> Nuevo Constructor
                </Link>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visibilidad</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {constructors.map((constructor) => (
                            <tr
                                key={constructor.id}
                                className="cursor-pointer hover:bg-blue-50 transition"
                                onClick={() => navigate(`/constructores/${constructor.id}`)}
                            >
                                <td className="px-6 py-4 whitespace-nowrap">{constructor.nombre}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{constructor.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{constructor.telefono}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        constructor.public ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {constructor.public ? 'Público' : 'Privado'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={e => e.stopPropagation()}>
                                    <button onClick={() => handleDeleteClick(constructor.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ConstructorListPage; 